import { HttpParams } from '@angular/common/http'
import { Component, effect, input } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellType, GridPanel } from '@grapecity/wijmo.grid'

import { BaseComponent, Grid, ListItem } from '../../../shared'
import { ActionToolbarCount } from '../../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../../shared/utility/auto-size.directive'

@Component({
    selector: 'app-estimate-vs-actual',
    templateUrl: './estimate-vs-actual.component.html',
    styleUrls: ['./estimate-vs-actual.component.css'],
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjInputModule,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        GridSelectionCalculationComponent,
    ],
})
export class EstimateVsActualComponent extends BaseComponent {
    active = input(0)
    faSearch = faSearch
    grid: Grid<any>

    allData: any[] = []
    scrollPosition: any

    startDate: Date = new Date(new Date().getFullYear() - 10, 0, 1)
    endDate: Date = new Date()
    minDate = new Date(2012, 0, 1)
    maxDate = new Date()

    activeMap: ListItem<number>[] = [
        new ListItem(1, 'Active'),
        new ListItem(2, 'Soft Closed'),
        new ListItem(3, 'Hard Closed'),
        new ListItem(0, 'Pending'),
    ]

    selectedGrouping = 'jobType'
    groupingOptions: ListItem<string>[] = [
        new ListItem('jobType', 'Job Type'),
        new ListItem('udPreQualified', 'PreQualified'),
        new ListItem('udSpecialized', 'Specialized'),
        new ListItem('projectManager', 'Project Manager'),
        new ListItem('siteSuper', 'Site Super'),
        new ListItem('pe', 'PE'),
        new ListItem('jca', 'JCA'),
        new ListItem('estimator', 'Estimator'),
    ]

    costTypesMap: ListItem<number>[] = []
    selectedCostType = 0

    boolMap: ListItem<boolean>[] = [new ListItem(true, 'Costs'), new ListItem(false, 'Hours')]
    isCost = true
    get gridFormat() {
        return !this.isCost && this.selectedCostType === 10 ? 'N0' : 'C0'
    }

    constructor() {
        super()
        this.activeMap[2].$checked = true

        this.grid = new Grid<any>(this.services, 'id', this).filterBy(item => [item.job])

        this.loadData()
        effect(() => {
            if (this.active() == 6 && this.isInitialLoading) {
                this.refresh()
            }
        })
    }
    loadData() {
        this.cancelableRequest(
            'loadData',
            true,
            this.services.data.load(this.active() === 6, true, this.services.data.get('/api/VpData/QueryCostTypes')).subscribe({
                next: result => {
                    const costTypes = result[0] as any[]
                    const costTypesMap = costTypes.map(x => new ListItem(x.costType, x.costType + ' - ' + x.description))
                    costTypesMap.unshift(new ListItem(0, 'All'))
                    this.costTypesMap = costTypesMap
                },
            }),
        )
    }
    refresh() {
        const params = new HttpParams()
            .set(
                'jobStatusString',
                this.activeMap
                    .filter(item => item.$checked)
                    .map(x => x.value)
                    .join(','),
            )
            .set('costType', this.selectedCostType)
            .set('isCost', this.isCost)
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(this.active() === 6, true, this.services.data.get('/api/Analytics/GetEstimateVsActual', params))
                .subscribe({
                    next: result => {
                        this.allData = result[0]
                        this.initialLoadingComplete()
                        this.groupRows()
                    },
                }),
        )
    }

    groupRows() {
        if (!this.grid.flexGrid) return

        // Save scroll position before reloading the grid.
        this.scrollPosition = this.grid.flexGrid.scrollPosition

        // Filter the data to only include the selected date range.
        const filteredData = this.allData.slice().filter(x => x.closeMonth >= this.startDate && x.closeMonth <= this.endDate)

        // group up the data by the selected grouping for lookup later
        const dataByGroup = filteredData.toObjectGroups(
            x => x[this.selectedGrouping],
            x => x,
        )

        const rows = [] as any[]

        for (const prop in dataByGroup) {
            // Add a mostly blank header row that just has the Group Name in the Job column
            rows.push({
                job: prop === 'null' ? 'Not Assigned' : prop,
                headerOrTotalRow: true,
            })

            // Add a row for each job for the grouping property.
            const categoryRows = [] as any[]
            for (const job of dataByGroup[prop]) {
                rows.push(job)
                categoryRows.push(job)
            }

            // Add a totals row that will need the totals by month and year for all jobs looped above.
            const totalEstimate = categoryRows.reduce((acc, x) => acc + x.estimate, 0)
            const totalDelta = categoryRows.reduce((acc, x) => acc + x.delta, 0)
            rows.push({
                job: 'Totals - ' + prop,
                headerOrTotalRow: true,
                estimate: totalEstimate,
                actual: categoryRows.reduce((acc, x) => acc + x.actual, 0),
                delta: totalDelta,
                percent: totalDelta / totalEstimate,
            })

            // Add a blank row to better distinquish the grouping rows.
            rows.push({
                job: '',
                headerOrTotalRow: true,
            })
        }

        // Now that we have added rows for all jobs, we can get the Totals rows and sum them into a grand total.
        const totalsRows = rows.filter(x => x.job.indexOf('Totals') > -1)
        const grandTotalEstimate = totalsRows.reduce((acc, x) => acc + x.estimate, 0)
        const grandTotalDelta = totalsRows.reduce((acc, x) => acc + x.delta, 0)
        rows.push({
            job: 'Grand Totals',
            headerOrTotalRow: true,
            estimate: grandTotalEstimate,
            actual: totalsRows.reduce((acc, x) => acc + x.actual, 0),
            delta: grandTotalDelta,
            percent: grandTotalDelta / grandTotalEstimate,
        })

        this.grid.items = rows
        this.grid.flexGrid.scrollPosition = this.scrollPosition
    }

    gridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const row = panel.grid.rows[r]
            const col = panel.grid.columns[c] as any
            const rowItem = row.dataItem

            cell.style.fontWeight = 'unset'
            if (rowItem.job.indexOf('Totals') > -1) {
                cell.style.fontWeight = '700'
            }
            if (col.binding == 'job' && rowItem.headerOrTotalRow) {
                cell.style.fontWeight = '700'
            }

            cell.style.color = 'unset'
            if (col.binding == 'delta') {
                if (rowItem.delta < 0) cell.style.color = 'red'
                else if (rowItem.delta > 0) cell.style.color = 'green'
            }

            if (col.binding == 'percent') {
                if (rowItem.percent < 0) cell.style.color = 'red'
                else if (rowItem.percent > 0) cell.style.color = 'green'
            }
        }
    }
    filter() {
        //
    }
}
